import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Box, Sparkles, Heart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";
import { toast } from "sonner";

const steps = [
  { title: "Welcome to Black Box", subtitle: "Your digital sanctuary for life's most meaningful moments." },
  { title: "Tell us about yourself", subtitle: "This helps us personalize your experience." },
  { title: "Your first reflection", subtitle: "Let's capture your first memory together." },
];

export default function OnboardingPage() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [name, setName] = useState(user?.name || "");
  const [bio, setBio] = useState("");
  const [firstMemory, setFirstMemory] = useState("");
  const [loading, setLoading] = useState(false);

  const handleNext = async () => {
    setLoading(true);
    try {
      if (step === 1) {
        await api.put("/profile", { name, bio });
        await api.put("/profile/onboarding", { step: 1, data: { name, bio } });
      } else if (step === 2) {
        if (firstMemory.trim()) {
          await api.post("/journal", {
            title: "My First Black Box Entry",
            content: firstMemory,
            mood: "reflective",
          });
        }
        await api.put("/profile/onboarding", { step: 3, data: {} });
        // Update user state directly with onboarding_complete
        const updatedUser = await api.get("/auth/me");
        if (updatedUser.data) {
          // Use setUser from context to avoid refresh race condition
          window.__bb_user_update = updatedUser.data;
        }
        await refreshUser();
        toast.success("Welcome to your vault!");
        navigate("/dashboard", { replace: true });
        return;
      } else {
        await api.put("/profile/onboarding", { step: step, data: {} });
      }
      setStep(step + 1);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const slideVariants = {
    enter: { x: 60, opacity: 0 },
    center: { x: 0, opacity: 1 },
    exit: { x: -60, opacity: 0 },
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center px-6" data-testid="onboarding-page">
      <div className="w-full max-w-lg">
        {/* Progress */}
        <div className="flex gap-2 mb-12">
          {steps.map((_, i) => (
            <div
              key={i}
              className={`h-1 flex-1 rounded-full transition-colors duration-500 ${
                i <= step ? "bg-[#00FF88]" : "bg-white/10"
              }`}
            />
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          >
            {step === 0 && (
              <div className="space-y-8">
                <div className="w-16 h-16 rounded-2xl bg-[#00FF88] flex items-center justify-center mb-8">
                  <Box className="w-8 h-8 text-black" />
                </div>
                <h1
                  className="text-4xl md:text-5xl font-bold tracking-tight leading-tight"
                  style={{ fontFamily: 'Manrope' }}
                >
                  {steps[0].title}
                </h1>
                <p className="text-slate-400 text-base md:text-lg leading-relaxed">
                  {steps[0].subtitle}
                </p>
                <div className="space-y-4 pt-4">
                  <div className="flex items-center gap-4 p-4 rounded-xl bg-white/5 border border-white/[0.06]">
                    <Sparkles className="w-5 h-5 text-[#00FF88] shrink-0" />
                    <p className="text-sm text-slate-300">Daily prompts to help you reflect and capture memories</p>
                  </div>
                  <div className="flex items-center gap-4 p-4 rounded-xl bg-white/5 border border-white/[0.06]">
                    <Heart className="w-5 h-5 text-[#00FF88] shrink-0" />
                    <p className="text-sm text-slate-300">Create legacy messages for the people you love most</p>
                  </div>
                </div>
              </div>
            )}

            {step === 1 && (
              <div className="space-y-8">
                <h1
                  className="text-4xl md:text-5xl font-bold tracking-tight leading-tight"
                  style={{ fontFamily: 'Manrope' }}
                >
                  {steps[1].title}
                </h1>
                <p className="text-slate-400 text-base md:text-lg">{steps[1].subtitle}</p>
                <div className="space-y-4">
                  <Input
                    placeholder="Your name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    data-testid="onboarding-name-input"
                    className="bg-white/5 border-white/10 focus:border-white/30 text-white placeholder:text-white/30 rounded-xl h-14 px-4"
                  />
                  <textarea
                    placeholder="A short bio about yourself (optional)"
                    value={bio}
                    onChange={(e) => setBio(e.target.value)}
                    data-testid="onboarding-bio-input"
                    className="w-full bg-white/5 border border-white/10 focus:border-white/30 text-white placeholder:text-white/30 rounded-xl p-4 h-28 resize-none outline-none focus:ring-1 focus:ring-[#00FF88]/30"
                  />
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-8">
                <h1
                  className="text-4xl md:text-5xl font-bold tracking-tight leading-tight"
                  style={{ fontFamily: 'Manrope' }}
                >
                  {steps[2].title}
                </h1>
                <p className="text-slate-400 text-base md:text-lg">{steps[2].subtitle}</p>
                <div className="p-6 rounded-2xl bg-white/5 border border-[#00FF88]/20">
                  <p className="text-sm text-[#00FF88] mb-3 font-medium">Your first prompt:</p>
                  <p className="text-white text-lg leading-relaxed" style={{ fontFamily: 'Manrope' }}>
                    What's one memory that always makes you smile, no matter what?
                  </p>
                </div>
                <textarea
                  placeholder="Write your thoughts here..."
                  value={firstMemory}
                  onChange={(e) => setFirstMemory(e.target.value)}
                  data-testid="onboarding-memory-input"
                  className="w-full bg-white/5 border border-white/10 focus:border-white/30 text-white placeholder:text-white/30 rounded-xl p-4 h-36 resize-none outline-none focus:ring-1 focus:ring-[#00FF88]/30"
                />
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        <div className="mt-12 flex justify-between items-center">
          {step > 0 ? (
            <button
              onClick={() => setStep(step - 1)}
              className="text-sm text-slate-500 hover:text-white transition-colors duration-200"
              data-testid="onboarding-back-btn"
            >
              Back
            </button>
          ) : (
            <div />
          )}
          <Button
            onClick={handleNext}
            disabled={loading || (step === 1 && !name.trim())}
            data-testid="onboarding-next-btn"
            className="bg-white text-black hover:bg-slate-200 rounded-full px-8 py-6 font-medium shadow-[0_0_20px_rgba(255,255,255,0.2)] hover:scale-105 active:scale-95 transition-transform duration-200"
          >
            {loading ? "..." : step === 2 ? "Enter Your Vault" : "Continue"}
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
}
